import { supabase } from '../../shared/supabase/supabaseClient';
import {
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  withReportsCache,
} from './reportsRemoteCache';
import { computeSalePaidPendingAmounts } from '../../shared/numeric/salePaymentAmounts';
import type { SaleDetail, SaleListRow, SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir, SaleCommissionFilter } from '../../sales/domain/salesTypes';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import { buildAfipQrUrl } from '../../fiscal/application/fiscalComprobanteFormat';
import { mapRpcError, rpcVentasProductFilters, scopeCacheKeyParts } from './reportsRemoteShared';
import { reportsQueryScopeHasFilters } from '../domain/reportsQueryScope';
import { parseModifierKind } from '../../catalog/domain-api';

export async function fetchRemoteListSales(params: {
  branchId: string;
  fromIso: string;
  toIso: string;
  search?: string;
  limit: number;
  offset: number;
  /** Misma semántica que SQLite (`CASHIER_LIST_FILTER_NULL`). */
  cashierUserIdFilter?: string;
  /** Filtro Atendió (`pos.sales.primary_staff_user_id`). */
  primaryStaffUserIdFilter?: string;
  /** Solo ventas con payment_status distinto de paid. */
  paymentPendingOnly?: boolean;
  /** Filtro por estado de venta (activa/anulada). */
  statusFilter?: SaleStatusFilter;
  /** Filtro por medio de cobro (method_id). */
  paymentMethodFilter?: string;
  /** Filtro por estado fiscal. */
  fiscalStatusFilter?: SaleFiscalStatusFilter;
  /** Canal: all | pos | online. */
  channelFilter?: 'all' | 'pos' | 'online';
  /** Filtro server-side por ventas con/sin comisión (`all` = sin filtro). */
  commissionFilter?: SaleCommissionFilter;
  /** Columna de ordenamiento. */
  sortKey?: SaleSortKey;
  /** Dirección de ordenamiento. */
  sortDir?: SaleSortDir;
  /** Alcance Informes (producto, categoría, cliente, operador, etc.). */
  queryScope?: ReportsQueryScope | null;
}): Promise<{ rows: SaleListRow[]; hasMore: boolean; totalCount: number | null } | null> {
  const scopeRpc = rpcVentasProductFilters(params.queryScope);
  const channel = params.channelFilter && params.channelFilter !== 'all' ? params.channelFilter : null;
  const commission =
    params.commissionFilter === 'with' || params.commissionFilter === 'without'
      ? params.commissionFilter
      : null;
  const hasScopeFilters = reportsQueryScopeHasFilters(params.queryScope ?? null);
  // Scope de Informes (producto/categoría) solo en el RPC canónico; canal en el RPC nuevo.
  const useChannelRpc = !hasScopeFilters;
  const rpcName = useChannelRpc ? 'reports_list_branch_sales_by_channel' : 'reports_list_branch_sales';
  return withReportsCache(
    reportsCacheKey([
      rpcName,
      params.branchId,
      params.fromIso,
      params.toIso,
      params.search,
      params.limit,
      params.offset,
      params.cashierUserIdFilter,
      params.primaryStaffUserIdFilter,
      params.paymentPendingOnly === true ? '1' : '0',
      params.statusFilter,
      params.paymentMethodFilter,
      params.fiscalStatusFilter,
      channel,
      commission,
      params.sortKey,
      params.sortDir,
      ...scopeCacheKeyParts(scopeRpc),
    ]),
    async () => {
      const primaryStaff = params.primaryStaffUserIdFilter ?? null;
      const rpcParams = useChannelRpc
        ? {
            p_branch_id: params.branchId,
            p_from: params.fromIso,
            p_to: params.toIso,
            p_search: params.search ?? null,
            p_limit: params.limit,
            p_offset: params.offset,
            p_cashier_user_id: scopeRpc.p_cashier_user_id ?? params.cashierUserIdFilter ?? null,
            p_payment_pending_only: params.paymentPendingOnly === true,
            p_status_filter: params.statusFilter ?? null,
            p_payment_method_filter: params.paymentMethodFilter ?? null,
            p_fiscal_status_filter: params.fiscalStatusFilter ?? null,
            p_sort_key: params.sortKey ?? null,
            p_sort_dir: params.sortDir ?? null,
            p_channel: channel,
            p_primary_staff_user_id: primaryStaff,
            p_commission_filter: commission,
          }
        : {
            p_branch_id: params.branchId,
            p_from: params.fromIso,
            p_to: params.toIso,
            p_search: params.search ?? null,
            p_limit: params.limit,
            p_offset: params.offset,
            p_cashier_user_id: scopeRpc.p_cashier_user_id ?? params.cashierUserIdFilter ?? null,
            p_payment_pending_only: params.paymentPendingOnly === true,
            p_status_filter: params.statusFilter ?? null,
            p_payment_method_filter: params.paymentMethodFilter ?? null,
            p_fiscal_status_filter: params.fiscalStatusFilter ?? null,
            p_sort_key: params.sortKey ?? null,
            p_sort_dir: params.sortDir ?? null,
            p_category: scopeRpc.p_category,
            p_item_type: scopeRpc.p_item_type,
            p_product_id: scopeRpc.p_product_id,
            p_brand: scopeRpc.p_brand,
            p_supplier_id: scopeRpc.p_supplier_id,
            p_customer_id: scopeRpc.p_customer_id,
            p_channel: channel,
            p_primary_staff_user_id: primaryStaff,
            p_commission_filter: commission,
          };
      const { data, error } = await supabase.rpc(rpcName, rpcParams);
      if (error) {
        console.warn(`[reportsRemote] ${rpcName}`, mapRpcError(error));
        return null;
      }
      const j = data as { rows?: unknown[]; has_more?: boolean; total_count?: number } | null;
      if (!j || !Array.isArray(j.rows)) return null;

      const rows: SaleListRow[] = (j.rows as Record<string, unknown>[]).map((r) => {
        const paymentStatus = String(r.payment_status ?? 'pending');
        const total = Number(r.total ?? 0);
        const nonCustomerPaid = Number(r.non_fiado_paid ?? 0);
        const customerAllocationPaid = Number(r.fiado_allocation_paid ?? 0);
        const { paidAmount, pendingAmount } = computeSalePaidPendingAmounts(
          total,
          paymentStatus,
          nonCustomerPaid,
          customerAllocationPaid
        );

        const paymentMethodIds = Array.isArray(r.payment_method_ids)
          ? (r.payment_method_ids as unknown[]).map((m) => String(m))
          : [];

        return {
          id: String(r.id ?? ''),
          counterNumber: r.counter_number != null ? Number(r.counter_number) : null,
          branchId: String(r.branch_id ?? ''),
          total,
          createdAt: String(r.created_at ?? new Date(0).toISOString()),
          synced: true,
          paymentStatus,
          fiscalStatus: String(r.fiscal_status ?? 'pending_sync'),
          fiscalMode: String(r.fiscal_mode ?? 'fiscal'),
          status: r.status === 'voided' ? 'voided' : 'active',
          channel: r.channel === 'online' ? 'online' : 'pos',
          customerOrderId: r.customer_order_id != null ? String(r.customer_order_id) : null,
          voidedAt: r.voided_at != null ? String(r.voided_at) : null,
          voidReason: r.void_reason != null ? String(r.void_reason) : null,
          voidedByUserId: r.voided_by_user_id != null ? String(r.voided_by_user_id) : null,
          itemCount: Number(r.item_count ?? 0),
          firstItemLabel: r.first_item_label != null ? String(r.first_item_label) : null,
          paymentMethodIds,
          cashierUserId: r.cashier_user_id != null ? String(r.cashier_user_id) : null,
          fiadoCustomerId: r.fiado_customer_id != null ? String(r.fiado_customer_id) : null,
          fiadoCustomerName: r.fiado_customer_name != null ? String(r.fiado_customer_name) : null,
          customerId: r.customer_id != null ? String(r.customer_id) : null,
          customerName: r.customer_name != null ? String(r.customer_name) : null,
          paidAmount,
          pendingAmount,
          invoiceCae: r.invoice_cae != null ? String(r.invoice_cae) : null,
          invoiceIssuedAt: r.invoice_issued_at != null ? String(r.invoice_issued_at) : null,
          invoiceStatus: r.invoice_status != null ? String(r.invoice_status) : null,
        };
      });

      const totalCount =
        typeof j.total_count === 'number' && Number.isFinite(j.total_count)
          ? Math.max(0, Math.trunc(j.total_count))
          : null;
      return { rows, hasMore: !!j.has_more, totalCount };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'sales_read', name: rpcName }
  );
}

export async function fetchRemoteSaleDetail(saleId: string): Promise<SaleDetail | null> {
  return withReportsCache(
    reportsCacheKey(['reports_get_sale_detail', saleId]),
    async () => {
      const { data, error } = await supabase.rpc('reports_get_sale_detail', {
        p_sale_id: saleId,
      });
      if (error) {
        console.warn('[reportsRemote] reports_get_sale_detail', mapRpcError(error));
        return null;
      }
      const j = data as {
        sale?: Record<string, unknown>;
        items?: Record<string, unknown>[];
        payments?: Record<string, unknown>[];
        discounts?: Record<string, unknown>[];
      } | null;
      if (!j?.sale) return null;

      const s = j.sale;
      const items = (j.items ?? []).map((it) => {
    const rawOpts = Array.isArray(it.options) ? it.options : [];
    const options =
      rawOpts.length > 0
        ? rawOpts.map((raw) => {
            const o = raw as Record<string, unknown>;
            const rawKind = o.modifierKind ?? o.modifier_kind;
            const rawTags = o.allergenTags ?? o.allergen_tags;
            const rawLinked = o.linkedProductId ?? o.linked_product_id;
            const rawKitchenLabel = o.kitchenLabel ?? o.kitchen_label;
            const rawSubs = o.substitutesChoiceId ?? o.substitutes_choice_id;
            return {
              groupId: String(o.groupId ?? o.group_id ?? ''),
              groupName: String(o.groupName ?? o.group_name ?? ''),
              choiceId: String(o.choiceId ?? o.choice_id ?? ''),
              choiceName: String(o.choiceName ?? o.choice_name ?? ''),
              priceDelta: Number(o.priceDelta ?? o.price_delta) || 0,
              qty: Math.max(1, Number(o.qty) || 1),
              linkedProductId: rawLinked != null ? String(rawLinked) : null,
              modifierKind: parseModifierKind(rawKind),
              allergenTags: Array.isArray(rawTags)
                ? rawTags.map(String).filter(Boolean)
                : [],
              kitchenLabel: rawKitchenLabel != null ? String(rawKitchenLabel) : null,
              substitutesChoiceId: rawSubs != null ? String(rawSubs) : null,
            };
          })
        : null;
    return {
      id: String(it.id ?? ''),
      productId: String(it.product_id ?? ''),
      quantity: Number(it.quantity ?? 0),
      price: Number(it.price ?? 0),
      unitPriceOriginal: it.unit_price_original != null ? Number(it.unit_price_original) : null,
      discountAmount: Number(it.discount_amount ?? 0),
      promotionId: it.promotion_id != null ? String(it.promotion_id) : null,
      label: it.label != null ? String(it.label) : null,
      sku: it.sku != null ? String(it.sku) : null,
      itemType: (it.item_type as 'product' | 'custom') ?? 'product',
      unitOfMeasure: it.unit_of_measure != null ? String(it.unit_of_measure) : null,
      options,
      lineNote:
        it.line_note != null && String(it.line_note).trim()
          ? String(it.line_note).trim()
          : it.lineNote != null && String(it.lineNote).trim()
            ? String(it.lineNote).trim()
            : null,
      performedByUserId: it.performed_by_user_id != null ? String(it.performed_by_user_id) : null,
      soldByUserId: it.sold_by_user_id != null ? String(it.sold_by_user_id) : null,
    };
  });

  const discounts = (j.discounts ?? []).map((d) => ({
    id: String(d.id ?? ''),
    promotionId: d.promotion_id != null ? String(d.promotion_id) : null,
    label: d.label != null ? String(d.label) : 'Descuento',
    discountAmount: Number(d.discount_amount ?? 0),
    scope: (d.scope as 'line' | 'category' | 'cart' | 'manual') ?? 'cart',
    metadata: (d.metadata && typeof d.metadata === 'object' ? (d.metadata as Record<string, unknown>) : null) ?? null,
  }));

  const payments = (j.payments ?? []).map((p) => ({
    id: String(p.id ?? ''),
    methodId: String(p.method_id ?? ''),
    amount: Number(p.amount ?? 0),
    status: String(p.status ?? 'confirmed'),
    reference: p.reference != null ? String(p.reference) : null,
    customerId: p.customer_id != null ? String(p.customer_id) : null,
    metadata: (p.metadata && typeof p.metadata === 'object' ? (p.metadata as Record<string, unknown>) : null) ?? null,
    createdAt: p.created_at != null ? String(p.created_at) : null,
  }));

  const paymentStatus = String(s.payment_status ?? 'pending');
  const total = Number(s.total ?? 0);
  const { paidAmount, pendingAmount } = computeSalePaidPendingAmounts(
    total,
    paymentStatus,
    Number(s.non_fiado_paid ?? 0),
    Number(s.fiado_allocation_paid ?? 0)
  );

  return {
    id: String(s.id ?? ''),
    branchId: String(s.branch_id ?? ''),
    total,
    subtotal: s.subtotal != null ? Number(s.subtotal) : null,
    discountTotal: Number(s.discount_total ?? 0),
    createdAt: String(s.created_at ?? ''),
    synced: true,
    paymentStatus,
    fiscalStatus: String(s.fiscal_status ?? 'pending_sync'),
    fiscalMode: String(s.fiscal_mode ?? 'fiscal'),
    status: s.status === 'voided' ? 'voided' : 'active',
    voidedAt: s.voided_at != null ? String(s.voided_at) : null,
    voidReason: s.void_reason != null ? String(s.void_reason) : null,
    voidedByUserId: s.voided_by_user_id != null ? String(s.voided_by_user_id) : null,
    cashSessionId: s.cash_session_id != null ? String(s.cash_session_id) : null,
    cashierUserId: s.cashier_user_id != null ? String(s.cashier_user_id) : null,
    tipAmount: s.tip_amount != null ? Math.max(0, Number(s.tip_amount) || 0) : undefined,
    primaryStaffUserId:
      s.primary_staff_user_id != null ? String(s.primary_staff_user_id) : null,
    channel: s.channel === 'online' ? 'online' : 'pos',
    counterNumber: s.counter_number != null ? Number(s.counter_number) : null,
    pickupName: s.pickup_name != null ? String(s.pickup_name) : null,
    fulfillmentType: s.fulfillment_type != null ? String(s.fulfillment_type) : null,
    customerOrderId: s.customer_order_id != null ? String(s.customer_order_id) : null,
    items,
    payments,
    discounts,
    fiadoCustomerId: s.fiado_customer_id != null ? String(s.fiado_customer_id) : null,
    fiadoCustomerName: s.fiado_customer_name != null ? String(s.fiado_customer_name) : null,
    customerId: s.customer_id != null ? String(s.customer_id) : null,
    customerName: s.customer_name != null ? String(s.customer_name) : null,
    paidAmount,
    pendingAmount,
    invoiceCae: s.invoice_cae != null ? String(s.invoice_cae) : null,
    invoiceIssuedAt: s.invoice_issued_at != null ? String(s.invoice_issued_at) : null,
    invoiceStatus: s.invoice_status != null ? String(s.invoice_status) : null,
    invoiceQrPayload: s.invoice_qr_payload != null ? String(s.invoice_qr_payload) : null,
    invoiceQrUrl: buildAfipQrUrl(
      s.invoice_qr_payload != null ? String(s.invoice_qr_payload) : null,
      null,
      {
        buyerCuit: s.invoice_buyer_cuit != null ? String(s.invoice_buyer_cuit) : null,
        cae: s.invoice_cae != null ? String(s.invoice_cae) : null,
      }
    ),
    invoiceType: s.invoice_type != null ? Number(s.invoice_type) : null,
    invoicePointOfSale: s.invoice_point_of_sale != null ? Number(s.invoice_point_of_sale) : null,
    invoiceNumber: s.invoice_number != null ? Number(s.invoice_number) : null,
    invoiceBuyerCuit: s.invoice_buyer_cuit != null ? String(s.invoice_buyer_cuit) : null,
    invoiceBuyerName: s.invoice_buyer_name != null ? String(s.invoice_buyer_name) : null,
    invoiceCaeExpiration: s.invoice_cae_expiration != null ? String(s.invoice_cae_expiration) : null,
    emitterCuit: s.emitter_cuit != null ? String(s.emitter_cuit) : null,
    emitterBusinessName: s.emitter_business_name != null ? String(s.emitter_business_name) : null,
    emitterFiscalEnvironment:
      s.emitter_fiscal_environment != null ? String(s.emitter_fiscal_environment) : null,
    invoiceNetAmount: s.invoice_net_amount != null ? Number(s.invoice_net_amount) : null,
    invoiceIvaAmount: s.invoice_iva_amount != null ? Number(s.invoice_iva_amount) : null,
    creditNoteStatus: s.credit_note_status != null ? String(s.credit_note_status) : null,
    creditNoteCae: s.credit_note_cae != null ? String(s.credit_note_cae) : null,
    creditNoteIssuedAt: s.credit_note_issued_at != null ? String(s.credit_note_issued_at) : null,
    creditNoteStatusInvoice:
      s.credit_note_status_invoice != null ? String(s.credit_note_status_invoice) : null,
    hasCustomerAllocations: Number(s.fiado_allocation_paid ?? 0) > 0,
    customerAllocationPaid: Number(s.fiado_allocation_paid ?? 0),
  };
    },
    REPORTS_REMOTE_CACHE_MS,
    { category: 'sales_read', name: 'reports_get_sale_detail' }
  );
}
